/**
 * Default API Client Instance
 *
 * Pre-configured API client that integrates with Firebase Auth
 * for automatic JWT token attachment.
 */

import { createApiClient, type ApiClient } from './client';
import type {
  ApiResponse,
  SignupRequest,
  SignupResponse,
  LoginRequest,
  LoginResponse,
  PasswordResetRequest,
  PasswordResetResponse,
  CurrentUserResponse,
  LogoutResponse,
  HealthCheckResponse,
  WhiteboardType,
  GetConfigurationsResponse,
  GetConfigurationResponse,
} from './types';

// Get API base URL from environment variable
// Only use localhost fallback in development to avoid triggering local network access prompts in production
const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? 'http://localhost:3001' : '');

// Warn in production if API URL is not configured (but don't crash - mock mode may be enabled)
if (!API_BASE_URL && !import.meta.env.DEV) {
  console.warn(
    '[API] VITE_API_BASE_URL is not configured. API calls will fail unless VITE_MOCK_API is enabled.'
  );
}

// Token getter function - will be set by AuthProvider
let tokenGetter: (() => Promise<string | null>) | null = null;

// Unauthorized callback - will be set by AuthProvider
let unauthorizedCallback: (() => void) | null = null;

/**
 * Set the token getter function (called by AuthProvider)
 */
export function setTokenGetter(getter: () => Promise<string | null>): void {
  tokenGetter = getter;
}

/**
 * Set the unauthorized callback (called by AuthProvider)
 */
export function setUnauthorizedCallback(callback: () => void): void {
  unauthorizedCallback = callback;
}

/**
 * Get the current auth token
 */
async function getToken(): Promise<string | null> {
  if (!tokenGetter) {
    return null;
  }
  return tokenGetter();
}

/**
 * Handle unauthorized responses
 */
function handleUnauthorized(): void {
  if (unauthorizedCallback) {
    unauthorizedCallback();
  }
}

/**
 * The default API client instance
 */
export const apiClient: ApiClient = createApiClient({
  baseUrl: API_BASE_URL,
  timeout: 30000,
  retries: 3,
  getToken,
  onUnauthorized: handleUnauthorized,
});

// ============================================================================
// Auth API Methods
// ============================================================================

/**
 * Auth API namespace
 */
export const authApi = {
  /**
   * Sign up a new user
   */
  signup(data: SignupRequest): Promise<ApiResponse<SignupResponse>> {
    return apiClient.post<ApiResponse<SignupResponse>>('/api/auth/signup', data, {
      skipAuth: true,
    });
  },

  /**
   * Login / verify token
   */
  login(data: LoginRequest): Promise<ApiResponse<LoginResponse>> {
    return apiClient.post<ApiResponse<LoginResponse>>('/api/auth/login', data, {
      skipAuth: true,
    });
  },

  /**
   * Logout (revoke refresh tokens)
   */
  logout(): Promise<ApiResponse<LogoutResponse>> {
    return apiClient.post<ApiResponse<LogoutResponse>>('/api/auth/logout');
  },

  /**
   * Request password reset
   */
  resetPassword(data: PasswordResetRequest): Promise<ApiResponse<PasswordResetResponse>> {
    return apiClient.post<ApiResponse<PasswordResetResponse>>('/api/auth/reset-password', data, {
      skipAuth: true,
    });
  },

  /**
   * Get current user
   */
  me(): Promise<ApiResponse<CurrentUserResponse>> {
    return apiClient.get<ApiResponse<CurrentUserResponse>>('/api/auth/me');
  },
};

// ============================================================================
// Health API Methods
// ============================================================================

/**
 * Health API namespace
 */
export const healthApi = {
  /**
   * Check server health
   */
  check(): Promise<HealthCheckResponse> {
    return apiClient.get<HealthCheckResponse>('/health', { skipAuth: true });
  },
};

// ============================================================================
// Configurations API Methods (Teacher Settings Persistence)
// ============================================================================

/**
 * Configurations API namespace
 */
export const configurationsApi = {
  /**
   * Get all configurations for the authenticated teacher
   */
  getAll(): Promise<ApiResponse<GetConfigurationsResponse>> {
    return apiClient.get<ApiResponse<GetConfigurationsResponse>>('/api/configurations');
  },

  /**
   * Get a specific configuration by whiteboard type
   */
  get(type: WhiteboardType): Promise<ApiResponse<GetConfigurationResponse>> {
    return apiClient.get<ApiResponse<GetConfigurationResponse>>(`/api/configurations/${type}`);
  },

  /**
   * Save a specific configuration
   */
  save(type: WhiteboardType, config: Record<string, unknown>): Promise<ApiResponse<GetConfigurationResponse>> {
    return apiClient.put<ApiResponse<GetConfigurationResponse>>(`/api/configurations/${type}`, { config });
  },

  /**
   * Save all configurations at once (bulk save)
   */
  saveAll(configurations: Record<string, Record<string, unknown>>): Promise<ApiResponse<GetConfigurationsResponse>> {
    return apiClient.put<ApiResponse<GetConfigurationsResponse>>('/api/configurations', { configurations });
  },

  /**
   * Delete a specific configuration
   */
  delete(type: WhiteboardType): Promise<ApiResponse<{ message: string }>> {
    return apiClient.delete<ApiResponse<{ message: string }>>(`/api/configurations/${type}`);
  },
};

// ============================================================================
// Re-export types for convenience
// ============================================================================

export type {
  ApiResponse,
  ApiErrorResponse,
  PaginationParams,
  PaginatedResponse,
  SignupRequest,
  SignupResponse,
  LoginRequest,
  LoginResponse,
  PasswordResetRequest,
  PasswordResetResponse,
  CurrentUserResponse,
  LogoutResponse,
  HealthCheckResponse,
  WhiteboardType,
  WB01ConfigData,
  WB02ConfigData,
  ConfigPresetData,
  TeacherSettingsData,
  ConfigurationRecord,
  GetConfigurationsResponse,
  GetConfigurationResponse,
  SaveConfigurationRequest,
  SaveAllConfigurationsRequest,
} from './types';
